import { inject } from "ui-common/di";
import type { IWritableBackofficePostsState, IBackofficePostsService } from "./types";
import type { IRpcService } from "ui-common/services/rpc/types";
import type { ISseService } from "ui-common/services/sse/types";
import type { PostMutatedEvent, WorldMutatedEvent } from "ui-common/services/sse/sse-events";
import type { PostsQueryResult } from "api-contracts/rpc/posts-query";
import type { GetPostResult } from "api-contracts/rpc/get-post";
import type { CreatePostParams, CreatePostResult } from "api-contracts/rpc/create-post";
import type { UpdatePostParams, UpdatePostResult } from "api-contracts/rpc/update-post";
import type { UpdateContentTierResult } from "api-contracts/rpc/update-content-tier";
import { NotificationService } from "ui-components/notification/notification-service";

export class BackofficePostsService implements IBackofficePostsService {
  @inject<IWritableBackofficePostsState>("writableBackofficePostsState")
  private state!: IWritableBackofficePostsState;

  @inject<IRpcService>("rpcService")
  private rpcService!: IRpcService;

  @inject<ISseService>("sseService")
  private sseService!: ISseService;

  private isLoaded = false;
  private unsubscribeSse: (() => void) | null = null;

  loadPosts(): void {
    if (this.isLoaded) return;

    this.state.loading.set(true);

    this.rpcService
      .call<PostsQueryResult>("postsQuery", undefined)
      .then((result) => {
        this.state.posts.set(result);
        this.isLoaded = true;
        this.subscribe();
      })
      .catch((error) => {
        console.error("[BackofficePosts] Failed to load posts:", error);
      })
      .finally(() => {
        this.state.loading.set(false);
      });
  }

  reloadPosts(): void {
    this.state.loading.set(true);

    this.rpcService
      .call<PostsQueryResult>("postsQuery", undefined)
      .then((result) => {
        this.state.posts.set(result);
        this.isLoaded = true;
      })
      .catch((error) => {
        console.error("[BackofficePosts] Failed to reload posts:", error);
      })
      .finally(() => {
        this.state.loading.set(false);
      });
  }

  loadPost(postId: string): void {
    const currentLoading = this.state.singlePostLoading.get();
    if (currentLoading.has(postId)) return;

    const newLoading = new Set(currentLoading);
    newLoading.add(postId);
    this.state.singlePostLoading.set(newLoading);

    this.rpcService
      .call<GetPostResult>("getPost", { postId })
      .then((result) => {
        const currentMap = new Map(this.state.singlePost.get());
        currentMap.set(postId, result);
        this.state.singlePost.set(currentMap);
      })
      .catch((error) => {
        console.error("[BackofficePosts] Failed to load post:", error);
      })
      .finally(() => {
        const loading = new Set(this.state.singlePostLoading.get());
        loading.delete(postId);
        this.state.singlePostLoading.set(loading);
      });
  }

  async createPost(params: CreatePostParams): Promise<CreatePostResult | undefined> {
    const result = await this.rpcService.call<CreatePostResult>("createPost", params);

    if (!result?.postId) {
      return result;
    }

    if (params.contentTier === "premium") {
      const tierResponse = await this.rpcService.call<UpdateContentTierResult>("update-content-tier", {
        contentType: "post",
        contentId: result.postId,
        tier: "premium",
      });

      if (tierResponse?.tierCreated && tierResponse?.defaultPriceAssigned) {
        NotificationService.info(
          `Premium subscription enabled! Default price set to \u20AC${(tierResponse.defaultPriceAssigned / 100).toFixed(2)}/month. You can change this in Settings.`,
        );
      }
    }

    if (params.oneTimePriceCents && params.contentTier === "premium") {
      await this.rpcService.call("enable-one-time-purchase", {
        contentType: "post",
        contentId: result.postId,
        priceCents: params.oneTimePriceCents,
        productName: `Premium Post - ${params.description.substring(0, 50)}`,
      });
    }

    this.reloadPosts();
    return result;
  }

  async updatePost(params: UpdatePostParams): Promise<UpdatePostResult | undefined> {
    const result = await this.rpcService.call<UpdatePostResult>("updatePost", params);

    if (!result?.postId) {
      return result;
    }

    if (params.contentTier === "premium") {
      const tierResponse = await this.rpcService.call<UpdateContentTierResult>("update-content-tier", {
        contentType: "post",
        contentId: result.postId,
        tier: "premium",
      });

      if (tierResponse?.tierCreated && tierResponse?.defaultPriceAssigned) {
        NotificationService.info(
          `Premium subscription enabled! Default price set to \u20AC${(tierResponse.defaultPriceAssigned / 100).toFixed(2)}/month. You can change this in Settings.`,
        );
      }
    } else if (params.contentTier === "free") {
      await this.rpcService.call("update-content-tier", {
        contentType: "post",
        contentId: result.postId,
        tier: "free",
      });
    }

    if (params.oneTimePriceCents && params.contentTier === "premium") {
      await this.rpcService.call("enable-one-time-purchase", {
        contentType: "post",
        contentId: result.postId,
        priceCents: params.oneTimePriceCents,
        productName: `Premium Post - ${params.teaser.substring(0, 50)}`,
      });
    }

    this.reloadPosts();
    return result;
  }

  async deletePost(postId: string): Promise<void> {
    await this.rpcService.call("deletePost", { postId });
    this.reloadPosts();
  }

  private subscribe(): void {
    if (this.unsubscribeSse) return;

    const unsubPost = this.sseService.on<PostMutatedEvent>("post-mutated", () => {
      this.reloadPosts();
    });

    const unsubWorld = this.sseService.on<WorldMutatedEvent>("world-mutated", (data) => {
      if (data.action === "updated" || data.action === "deleted") {
        this.reloadPosts();
      }
    });

    this.unsubscribeSse = () => {
      unsubPost();
      unsubWorld();
    };
  }

  clearCache(): void {
    this.unsubscribeSse?.();
    this.unsubscribeSse = null;
    this.state.posts.set(null);
    this.state.singlePost.set(new Map());
    this.state.singlePostLoading.set(new Set());
    this.state.loading.set(false);
    this.isLoaded = false;
  }
}
